import { AppLayout } from "@/components/layout/app-layout";
import { PageHeader } from "@/components/layout/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AddClientDialog } from "@/components/client-portfolio/add-client-dialog";
import { useState } from "react";

const ClientPortfolio = () => {
  const { toast } = useToast();
  const [addClientOpen, setAddClientOpen] = useState(false);
  
  // Fetch real mandates data from Supabase
  const { data: mandates = [], isLoading, refetch } = useQuery({
    queryKey: ['mandates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mandates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    }
  });

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case "Low": return "bg-green-500/20 text-green-700";
      case "Moderate": return "bg-yellow-500/20 text-yellow-700";
      case "High": return "bg-red-500/20 text-red-700";
      default: return "bg-gray-500/20 text-gray-700";
    }
  };

  return (
    <AppLayout>
      <PageHeader 
        title="Client Portfolio"
        subtitle="Comprehensive overview of client portfolios and performance"
      />
      
      <main className="flex-1 p-page-padding lg:p-page-padding-lg space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Client Portfolio</h1>
              <p className="text-muted-foreground mt-1">
                Comprehensive overview of client portfolios and performance
              </p>
            </div>
            <Button onClick={() => setAddClientOpen(true)}>Add New Client</Button>
          </div>

          {/* Portfolio Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Active Mandates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{mandates.filter(m => m.status === 'active').length}</div>
                <p className="text-xs text-muted-foreground">Currently active</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Mandates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{mandates.length}</div>
                <p className="text-xs text-muted-foreground">All mandates</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Pending Review</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{mandates.filter(m => m.status === 'pending').length}</div>
                <p className="text-xs text-muted-foreground">Awaiting approval</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">This Month</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {mandates.filter(m => {
                    const created = new Date(m.created_at);
                    const now = new Date();
                    return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
                  }).length}
                </div>
                <p className="text-xs text-muted-foreground">New this month</p>
              </CardContent>
            </Card>
          </div>

          {/* Mandate List */}
          <Card>
            <CardHeader>
              <CardTitle>Mandate Overview</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8 text-muted-foreground">Loading mandates...</div>
              ) : mandates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No mandates found. Create your first mandate to get started.
                </div>
              ) : (
                <div className="space-y-4">
                  {mandates.map((mandate) => (
                    <div key={mandate.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors">
                      <div className="flex items-center space-x-4">
                        <Avatar>
                          <AvatarFallback>{mandate.client_name?.substring(0, 2).toUpperCase() || 'UN'}</AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-semibold">{mandate.client_name || 'Unknown Client'}</h3>
                          <p className="text-sm text-muted-foreground">Type: {mandate.mandate_type || 'N/A'}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-4">
                        <Badge variant={mandate.status === "active" ? "default" : "secondary"}>
                          {mandate.status}
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            toast({
                              title: "Mandate Details",
                              description: `Opening details for ${mandate.client_name}...`,
                            });
                          }}
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status Distribution */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Status Distribution</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Active</span>
                    <span>{mandates.filter(m => m.status === 'active').length}</span>
                  </div>
                  <Progress
                    value={mandates.length > 0 ? (mandates.filter(m => m.status === 'active').length / mandates.length) * 100 : 0}
                    className="h-2"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Pending</span>
                    <span>{mandates.filter(m => m.status === 'pending').length}</span>
                  </div>
                  <Progress
                    value={mandates.length > 0 ? (mandates.filter(m => m.status === 'pending').length / mandates.length) * 100 : 0}
                    className="h-2"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Inactive</span>
                    <span>{mandates.filter(m => m.status === 'inactive').length}</span>
                  </div>
                  <Progress
                    value={mandates.length > 0 ? (mandates.filter(m => m.status === 'inactive').length / mandates.length) * 100 : 0}
                    className="h-2"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recent Mandates</CardTitle>
              </CardHeader>
              <CardContent>
                {mandates.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No mandates available</div>
                ) : (
                  <div className="space-y-3">
                    {mandates.slice(0, 3).map((mandate) => (
                      <div key={mandate.id} className="flex justify-between items-center">
                        <span className="text-sm">{mandate.client_name || 'Unknown'}</span>
                        <Badge variant="secondary" className="text-xs">
                          {new Date(mandate.created_at).toLocaleDateString()}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
      </main>
      
      <AddClientDialog
        open={addClientOpen}
        onOpenChange={setAddClientOpen}
        onSuccess={() => {
          refetch();
          toast({
            title: "Success",
            description: "Client added successfully.",
          });
        }}
      />
    </AppLayout>
  );
};

export default ClientPortfolio;